import {
  Dropdown,
  FormController,
  FormLabel,
  MenuItem,
  Typography,
} from '@one-thd/sui-atomic-components';
import { Image } from '@one-thd/sui-icons';
import { AxiosError } from 'axios';
import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  GetTopSpendByItemQuantity,
  GetTopSpendByItemSpend,
} from '../../hooks/business-tools-hooks';
import {
  useBusinessToolsContext,
  useCommonContext,
} from '../../hooks/useContexts';

import {
  IBusinessToolsRequest,
  IContextData,
  IDenominatorAmount,
  IItemQuantity,
  IItemSpend,
  ITopItemsState,
  TSelectedValue,
} from '../../models/business-tools';
import { DateRanges } from '../../utils/DateRanges';
import { getSvocId } from '../../utils/user';
import { SectionHeader } from '../shared/section-header/SectionHeader';
import { BusinessToolsErrorMessage } from './BuisnessToolsErrorMessage';

import '../../styles/business-tools.scss';
import { TopItemSkeleton } from './BusinessToolsSkeletons';

const styleBars = (widthNumber: number) => {
  if (widthNumber <= 0.2) {
    return widthNumber > 0 ? { width: '0.2%' } : { width: '0%' };
  }
  return { width: `${widthNumber}%` };
};

const compareAmounts = (
  prevItem: IItemSpend[] | IItemQuantity[] | string | undefined,
  newItem: IItemSpend[] | IItemQuantity[] | string | undefined,
) => {
  const property =
    prevItem === 'totalTransactionAmount'
      ? 'totalTransactionAmount'
      : 'totalQuantity';
  if (
    (prevItem as unknown as IItemSpend | IItemQuantity)[
      property as keyof (IItemSpend | IItemQuantity)
    ] >
    (newItem as unknown as IItemSpend | IItemQuantity)[
      property as keyof (IItemSpend | IItemQuantity)
    ]
  )
    return -1;
  if (
    (prevItem as unknown as IItemSpend | IItemQuantity)[
      property as keyof (IItemSpend | IItemQuantity)
    ] <
    (newItem as unknown as IItemSpend | IItemQuantity)[
      property as keyof (IItemSpend | IItemQuantity)
    ]
  )
    return 1;
  return 0;
};

// function to calculate width for each element
const spendPercentage = (firstAmount: number, otherAmount?: number) => {
  if (firstAmount && otherAmount) {
    return (otherAmount / firstAmount) * 100;
  }
  return 0;
};

const getItemPercentage = (
  data: IItemSpend & IItemQuantity,
  denominatorAmount: IDenominatorAmount,
): number => {
  if (data.totalTransactionAmount) {
    return spendPercentage(
      denominatorAmount.totalTransactionAmount,
      data?.totalTransactionAmount,
    );
  }
  return spendPercentage(denominatorAmount.totalQuantity, data?.totalQuantity);
};

const renderItemImage = (data: IItemSpend & IItemQuantity) => {
  if (data.imageURL) {
    return (
      <img
        width={75}
        height={75}
        className="business-tools__imageWidthHeight"
        src={data.imageURL}
        alt="item"
      />
    );
  }
  return (
    <div style={{ padding: 15 }}>
      {' '}
      <Image size="large" />
    </div>
  );
};

// TODO: Not currently being used, check if can be removed

// const getOptionsValues = (
//   data: IItemSpend & IItemQuantity,
//   contextData: IContextData,
// ) => {
//   if (contextData.dateSelectorValue === 'custom') {
//     return {
//       requestSearchSelectorValue: 'SKUNumber',
//       requestSearchCriteria: data.skuNumber,
//       startDate: contextData.startDate,
//       endDate: contextData.endDate,
//       dateSelectorValue: contextData.dateSelectorValue,
//     };
//   }
//   return {
//     requestSearchSelectorValue: 'SKUNumber',
//     requestSearchCriteria: data.skuNumber,
//     dateSelectorValue: contextData.dateSelectorValue,
//   };
// };

// map over array and return each element html and values
const topItemsArray = (
  topItemData: IItemSpend[] | IItemQuantity[] | undefined,
  redirectQueryParams: IContextData,
  showMoreItems: number,
  businessToolsHostName: string,
) => {
  // TODO: Fix any type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sortedArray = topItemData && topItemData.sort(compareAmounts as any);
  const denominatorAmount = sortedArray ? sortedArray[0] : 0;
  return sortedArray?.map((data: IItemSpend & IItemQuantity, i: number) => {
    const itemPercentage = getItemPercentage(
      data,
      denominatorAmount as IDenominatorAmount,
    );

    const operator =
      data.totalTransactionAmount && data.totalTransactionAmount < 0 ? '-' : '';

    if (redirectQueryParams?.poJobName !== 'all') {
      Object.assign(redirectQueryParams, {
        searchBy: [
          {
            SKUNumber: data.skuNumber,
            POJobName: redirectQueryParams?.poJobName,
          },
        ],
      });
    }

    if (showMoreItems === 0 && i > 4) {
      return true;
    }

    return (
      <ul>
        <div
          // TODO: Change to button once CPS can redirect with proper data. No ETA on this
          // type="button"
          // onClick={() => {
          //   getWindow()?.location.assign(redirectUrl);
          // }}
          className="business-tools__displayBlock"
          style={{ width: '100%' }}
        >
          <li className="u__clearfix business-tools__List" key={i}>
            <div className="col__2-12 col__1-12--xs business-tools__rmPadding">
              {renderItemImage(data)}
            </div>
            <div className="col__10-12 col__11-12--xs business-tools__addPaddingLeft15 business-tools__rmPaddingRight">
              <div className="sui-flex" style={{ width: '100%' }}>
                <h3>
                  <strong>{data.skuNumber}</strong>
                </h3>
                <p style={{ marginLeft: 5 }}>{data.shortSkuDescription}</p>
              </div>
              <div className="u__flex business-tools__spaceBetween">
                <p>{data.shortSkuDescription}</p>
                <p className="business-tools__fontWeight">
                  {!data.totalQuantity
                    ? `${operator}$${Math.abs(
                        data.totalTransactionAmount || 0,
                      ).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}`
                    : `${data.totalQuantity}`}
                </p>
              </div>
              <div className="bar-container">
                <div
                  className="business-tools__Bar business-tools__marginTop--topitems"
                  style={styleBars(itemPercentage)}
                />
              </div>
            </div>
          </li>
        </div>
      </ul>
    );
  });
};

// function to show more items
const showMoreItemsLogic = (
  setShowMoreJobs: (value: number) => void,
  setHideMe: (value: boolean) => void,
) => {
  setShowMoreJobs(1);
  setHideMe(false);
};

// function to show less items
const showLessItemsLogic = (
  setShowMoreJobs: (value: number) => void,
  setHideMe: (value: boolean) => void,
) => {
  setShowMoreJobs(0);
  setHideMe(true);
};

const renderItemSelectElement = (
  selectedValue: TSelectedValue,
  setSelectedValue: (value: TSelectedValue) => void,
) => {
  return (
    <FormController>
      <FormLabel>Show Items by</FormLabel>
      <Dropdown
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const itemSelectvalue = event.target.value as TSelectedValue;
          setSelectedValue(itemSelectvalue);
        }}
        value={selectedValue}
      >
        <MenuItem value="spend">Spend</MenuItem>
        <MenuItem value="quantity">Quantity</MenuItem>
      </Dropdown>
    </FormController>
  );
};

const renderSubHeader = (
  selectedValue: TSelectedValue,
  setSelectedValue: (value: TSelectedValue) => void,
) => {
  // business-tools__fullWidth u__flex business-tools__subHeader
  return (
    <div className="sui-flex sui-w-full sui-justify-between sui-text-subtle">
      <div className="sui-z-10">
        {renderItemSelectElement(selectedValue, setSelectedValue)}
      </div>

      <Typography variant="body-xs" className="business-tools__specialPadding">
        Displays Pre-Tax Dollar Spend
      </Typography>
      {/* <div className="select-box col__9-12 col__4-12--xs col__3-12--sm business-tools__rmPaddingRL">
       
      </div> */}
      {/* <div className="subheader-text u__flex business-tools__flexColumn">
        <h4 className="business-tools__specialPadding">
          Displays Pre-Tax Dollar Spend
        </h4>
      </div> */}
    </div>
  );
};

const renderMoreLessButtons = (
  topItems: IItemSpend[] | IItemQuantity[],
  lessMoreState: ITopItemsState,
) => {
  const { hideMe, setShowMoreItems, setHideMe } = lessMoreState;
  if (topItems && topItems.length > 5) {
    return (
      <div className="col__6-12 col__3-12--sm business-tools__rmPaddingLeft">
        {hideMe ? (
          <button
            type="button"
            className="bttn-outline bttn--link"
            id="showMoreItemsElement"
            onClick={() => showMoreItemsLogic(setShowMoreItems, setHideMe)}
          >
            <span className="bttn__content">Show more items</span>
          </button>
        ) : (
          <button
            type="button"
            className="bttn-outline bttn--link"
            id="showLessItemsElement"
            onClick={() => showLessItemsLogic(setShowMoreItems, setHideMe)}
          >
            <span className="bttn__content">Show less</span>
          </button>
        )}
      </div>
    );
  }
  return undefined;
};

const renderTopSpendByItemsElement = (
  topItems: (IItemSpend[] & IItemQuantity[]) | undefined,
  topItemsError: AxiosError | undefined,
  topItemsLoaded: boolean,
  redirectQueryParams: IContextData,
  topItemsState: ITopItemsState,
  businessToolsHostName: string,
) => {
  if (topItemsLoaded) {
    if (topItemsError) {
      return BusinessToolsErrorMessage();
    }
    if (topItems && topItems.length > 0) {
      return (
        <>
          {' '}
          {renderSubHeader(
            topItemsState.selectedValue,
            topItemsState.setSelectedValue,
          )}
          {topItemsArray(
            topItems,
            redirectQueryParams,
            topItemsState.showMoreItems,
            businessToolsHostName,
          )}
          {renderMoreLessButtons(topItems, topItemsState)}
        </>
      );
    }
    return (
      <Typography weight="bold">
        No records can be found for the selected date range and/or job name.
      </Typography>
    );
  }
  return <TopItemSkeleton />;
};

export const TopItems = () => {
  const [selectedValue, setSelectedValue] = useState<TSelectedValue>('spend');
  const [showMoreItems, setShowMoreItems] = useState(0);
  const [hideMe, setHideMe] = useState(true);

  const [topItems, setTopItems] = useState<IItemSpend[] & IItemQuantity[]>();
  const [topItemsError, setTopItemsError] = useState<AxiosError>();
  const [topItemsLoaded, setTopItemsLoaded] = useState<boolean>(false);

  const { dateRangeOptions, timezone } = useCommonContext();
  const { dateSelectorValue, requestData, businessToolsHostName } =
    useBusinessToolsContext();

  const request: IBusinessToolsRequest = {
    businessToolsRequest: {
      startDate:
        dateSelectorValue === 'custom'
          ? dateRangeOptions.startDate
          : DateRanges[dateSelectorValue].startDate,
      endDate:
        dateSelectorValue === 'custom'
          ? dateRangeOptions.endDate
          : DateRanges[dateSelectorValue].endDate,
      jobName: requestData.poJobName,
      timezone,
    },
    customerAccountId: getSvocId() || '',
  };

  useEffect(() => {
    if (selectedValue === 'spend') {
      GetTopSpendByItemSpend(
        request,
        setTopItems,
        setTopItemsError,
        setTopItemsLoaded,
      );
    } else if (selectedValue === 'quantity') {
      GetTopSpendByItemQuantity(
        request,
        setTopItems,
        setTopItemsError,
        setTopItemsLoaded,
      );
    }
  }, [
    request.businessToolsRequest.startDate,
    request.businessToolsRequest.endDate,
    request.businessToolsRequest.jobName,
    selectedValue,
  ]);

  /**
   * Created to carry common and businessTools context data for
   * sending query data to purchase history.
   */
  const redirectQueryParams: IContextData = {
    startDate:
      dateSelectorValue === 'custom'
        ? dateRangeOptions.startDate
        : DateRanges[dateSelectorValue].startDate,
    endDate:
      dateSelectorValue === 'custom'
        ? dateRangeOptions.endDate
        : DateRanges[dateSelectorValue].endDate,
    dateSelectorValue: dateSelectorValue || 'YTD',
    poJobName: requestData.poJobName || '',
  };

  const topItemsState: ITopItemsState = {
    selectedValue,
    setSelectedValue,
    showMoreItems,
    setShowMoreItems,
    hideMe,
    setHideMe,
  };

  return (
    <div className="top-items">
      <SectionHeader title="Top Items" />
      {renderTopSpendByItemsElement(
        topItems,
        topItemsError,
        topItemsLoaded,
        redirectQueryParams,
        topItemsState,
        businessToolsHostName,
      )}
    </div>
  );
};
